import { CancelToken } from 'axios';
import { useState } from 'react';
import { useNavigate } from 'react-router';
import { useAppDispatch, useAppSelector } from '../../store';
import { QcsTableCell } from '../common/basic/QcsTableCell';
import { SectionHeader } from '../common/SectionHeader';
import { Grid } from '../common/grid/Grid';
import {
  getInvitationTypeVisitList,
  invitationTypeVisitListActions,
  selectInvitationTypeVisitList,
} from '../../store/entities/invitationTypeVisitList';
import { InvitationTypeVisitListResponse } from '@qcs/safety-client';
import { CellDate } from '../common/grid/CellDate';
import { InvitationModal } from './InvitationModal';
import { faUserPlus } from '@fortawesome/free-solid-svg-icons';
import { QcsButton } from '../common/basic/QcsButton';
import { FaIcon } from '../common/FaIcon';
import { useCreateSupplierEmployee } from './useCreateSupplierEmployee';
import { selectIdentity } from '../../store/entities/identity';
import { hasRole } from '../../utils/roles';
import { UserRole } from '@api/models/enums/UserRole';

interface InvitationTypeVisitData extends InvitationTypeVisitListResponse {
  id?: string;
}

export const InvitationTypeVisit = () => {
  const [open, setOpen] = useState(false);
  const invitationTypeVisitList = useAppSelector(selectInvitationTypeVisitList);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const createSupplierEmployee = useCreateSupplierEmployee(navigate);
  const identity = useAppSelector(selectIdentity);

  const canAssignToSupplier = hasRole(identity.roles, [UserRole.AdminQcs, UserRole.ManagerOsah, UserRole.ManagerWorkplace]);

  const [selectedInvitationData, setSelectedInvitationData] = useState<InvitationTypeVisitData | null>(null);
  const handleGetData = (cancelToken: CancelToken) => {
    dispatch(getInvitationTypeVisitList(cancelToken));
  };

  const handleRenderData = (item: InvitationTypeVisitData) => {
    return (
      <>
        <QcsTableCell>{item.name}</QcsTableCell>
        <QcsTableCell>{item.lastName}</QcsTableCell>
        <QcsTableCell>{item.company}</QcsTableCell>
        <QcsTableCell>{item.email}</QcsTableCell>
        <QcsTableCell>{item.phone}</QcsTableCell>
        <CellDate value={item.lastEntry} showWithTime={true} />

        <QcsTableCell>
          {canAssignToSupplier && (
            <QcsButton
              stopPropagation
              onClick={() => {
                setSelectedInvitationData(item);
                setOpen(true);
              }}
            >
              <FaIcon icon={faUserPlus} />
            </QcsButton>
          )}
        </QcsTableCell>
      </>
    );
  };

  const handleRowClick = (item: InvitationTypeVisitData) => {
    navigate(`/visit/${item.supplierId}/employee/${item.supplierEmployeeId}`);
  };

  return (
    <>
      <SectionHeader title="invitationTypeVisit.title" />
      <InvitationModal
        open={open}
        onClose={() => setOpen(false)}
        onSupplierSelectClick={(supplier) => {
          createSupplierEmployee(supplier, selectedInvitationData!.supplierEmployeeId!, selectedInvitationData!);
        }}
      />
      <Grid<InvitationTypeVisitData>
        headers={[
          {
            captionStr: 'invitationTypeVisit.name',
            orderName: 'name',
          },
          {
            captionStr: 'invitationTypeVisit.lastName',
            orderName: 'lastName',
          },
          {
            captionStr: 'invitationTypeVisit.company',
            orderName: 'supplier',
          },
          {
            captionStr: 'invitationTypeVisit.email',
          },
          {
            captionStr: 'invitationTypeVisit.phone',
          },
          {
            captionStr: 'invitationTypeVisit.lastEntry',
          },
          {
            captionStr: '',
          },
        ]}
        data={invitationTypeVisitList}
        gridActions={invitationTypeVisitListActions}
        renderData={handleRenderData}
        onRowClick={handleRowClick}
        getData={handleGetData}
      />
    </>
  );
};
